<template>
  <v-navigation-drawer
    v-model="isVisible"
    right
    width="600"
    fixed
    temporary
    class="campaign-drawer">
    <div class="drawer-content">
      <div class="drawer-header">
        <div class="header-content">
          <h2 class="text-h5 text-bold drawer-title">
            New outbound campaign
          </h2>
          <v-btn icon class="close-btn" :disabled="saving" @click="onClose">
            <v-icon size="20">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </div>

      <div class="drawer-body">
        <section class="form-section">
          <h3 class="section-title">
            Campaign
          </h3>
          <div class="form-field">
            <label class="field-label">Name <span class="required">*</span></label>
            <MankTextField
              v-model="form.name"
              dense
              hide-details="auto"
              placeholder="e.g. October renewals"/>
          </div>
          <div class="form-field">
            <label class="field-label">Agent <span class="required">*</span></label>
            <MankSelect
              v-model="form.agentId"
              :items="agentItems"
              item-text="text"
              item-value="value"
              :loading="loadingAgents"
              dense
              hide-details="auto"
              placeholder="Select the agent that makes the calls"
              :menu-props="{ maxHeight: 300, offsetY: true, nudgeBottom: 8 }"/>
          </div>
          <div class="form-field">
            <label class="field-label">SIP outbound trunk ID <span class="required">*</span></label>
            <MankTextField
              v-model="form.sipTrunkId"
              dense
              hide-details="auto"
              placeholder="ST_…"/>
            <p class="helper-text">
              The LiveKit outbound trunk the calls are placed through. It must be a trunk you own.
            </p>
          </div>
        </section>

        <section class="form-section">
          <h3 class="section-title">
            Schedule &amp; pacing
          </h3>
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">Start at</label>
                <input v-model="form.startAt" type="datetime-local" class="native-input">
                <p class="helper-text">
                  Empty: as soon as the campaign is started.
                </p>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">End at</label>
                <input v-model="form.endAt" type="datetime-local" class="native-input">
                <p class="helper-text">
                  Numbers not called by then are cancelled.
                </p>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">Numbers per batch</label>
                <MankTextField
                  v-model.number="form.batchSize"
                  type="number"
                  :min="limits.batch.min"
                  :max="limits.batch.max"
                  dense
                  hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">Seconds between batches</label>
                <MankTextField
                  v-model.number="form.intervalSeconds"
                  type="number"
                  :min="limits.interval.min"
                  :max="limits.interval.max"
                  dense
                  hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">Attempts per number</label>
                <MankTextField
                  v-model.number="form.maxAttempts"
                  type="number"
                  :min="limits.attempts.min"
                  :max="limits.attempts.max"
                  dense
                  hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-field">
                <label class="field-label">Retry after (seconds)</label>
                <MankTextField
                  v-model.number="form.retryDelaySeconds"
                  type="number"
                  :min="limits.retryDelay.min"
                  :max="limits.retryDelay.max"
                  :disabled="form.maxAttempts <= 1"
                  dense
                  hide-details="auto"/>
              </div>
            </v-col>
          </v-row>
          <p class="pacing-summary">
            {{ pacingSummary }}
          </p>
        </section>

        <section class="form-section">
          <div class="section-title-row">
            <h3 class="section-title">
              Phone numbers
            </h3>
            <a href="#" class="template-link" @click.prevent="downloadTemplate">
              Download CSV template
            </a>
          </div>
          <div class="form-field">
            <label class="field-label">CSV file</label>
            <v-file-input
              :key="fileInputKey"
              v-model="form.csvFile"
              accept=".csv,text/csv"
              placeholder="Choose a CSV file"
              outlined
              dense
              clearable
              prepend-icon=""
              hide-details="auto"
              :error-messages="csvError"
              class="mank-input"/>
            <p class="helper-text">
              Needs a <code>phone_number</code> column (or phone, number, msisdn, telefon). A <code>name</code>
              column and any other columns are passed to the agent. Max 5 MB / 10,000 rows.
            </p>
          </div>
          <div class="form-field">
            <label class="field-label">Or paste numbers</label>
            <MankTextarea
              v-model="form.pastedNumbers"
              rows="3"
              dense
              hide-details="auto"
              placeholder="+905551234567, one per line or comma separated"/>
          </div>
          <p class="helper-text mb-0">
            Numbers must be in international format (+90…). More numbers can be added later.
          </p>
        </section>

        <section class="form-section form-section--last">
          <Checkbox v-model="form.startNow" label="Start the campaign right after creating it"/>
        </section>
      </div>

      <div class="drawer-footer">
        <div class="button-group">
          <v-btn
            color="surface-1"
            elevation="0"
            class="mank-btn"
            :disabled="saving"
            @click="onClose">
            <Icon name="cross" variant="solid" color="default" size="20"/>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            elevation="0"
            class="mank-btn"
            :loading="saving"
            :disabled="!canSubmit || saving"
            @click="submit">
            <Icon name="plus" variant="solid" color="onPrimary" size="20"/>
            Create campaign
          </v-btn>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import Checkbox from '@/components/Checkbox.vue';
import Icon from '@/components/Icon.vue';
import MankSelect from '@/components/MankSelect.vue';
import MankTextField from '@/components/MankTextField.vue';
import MankTextarea from '@/components/MankTextarea.vue';
import AgentService from '@/services/AgentService';
import OutboundCampaignService from '@/services/OutboundCampaignService';
import {
  CAMPAIGN_LIMITS,
  downloadCsvTemplate,
  formatSeconds,
  fromDateTimeLocal,
  notifyError,
  notifySuccess,
  parsePastedNumbers,
} from '@/constants/outboundCampaign';

const emptyForm = () => ({
  name: '',
  agentId: null,
  sipTrunkId: '',
  startAt: '',
  endAt: '',
  batchSize: CAMPAIGN_LIMITS.batch.default,
  intervalSeconds: CAMPAIGN_LIMITS.interval.default,
  maxAttempts: CAMPAIGN_LIMITS.attempts.default,
  retryDelaySeconds: CAMPAIGN_LIMITS.retryDelay.default,
  csvFile: null,
  pastedNumbers: '',
  startNow: false,
});

const inRange = (value, { min, max }) => Number.isInteger(value) && value >= min && value <= max;

export default {
  name: 'CreateOutboundCampaignDrawer',
  components: {
    Checkbox,
    Icon,
    MankSelect,
    MankTextField,
    MankTextarea,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: emptyForm(),
      agents: [],
      loadingAgents: false,
      saving: false,
      fileInputKey: 0,
      limits: CAMPAIGN_LIMITS,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value && !this.saving) this.$emit('close');
      },
    },
    agentItems() {
      return this.agents.map((a) => ({ text: a.name || a.id, value: a.id }));
    },
    csvError() {
      const f = this.form.csvFile;
      if (f && f.size > CAMPAIGN_LIMITS.csvMaxBytes) return 'File must be at most 5 MB';
      return [];
    },
    scheduleError() {
      const start = fromDateTimeLocal(this.form.startAt);
      const end = fromDateTimeLocal(this.form.endAt);
      if (end && new Date(end) <= new Date()) return 'End time must be in the future.';
      if (start && end && new Date(end) <= new Date(start)) return 'End time must be after the start time.';
      return '';
    },
    pacingError() {
      const f = this.form;
      const l = CAMPAIGN_LIMITS;
      if (!inRange(f.batchSize, l.batch)) return `Numbers per batch must be ${l.batch.min}–${l.batch.max}.`;
      if (!inRange(f.intervalSeconds, l.interval)) return `Seconds between batches must be ${l.interval.min}–${l.interval.max}.`;
      if (!inRange(f.maxAttempts, l.attempts)) return `Attempts per number must be ${l.attempts.min}–${l.attempts.max}.`;
      if (f.maxAttempts > 1 && !inRange(f.retryDelaySeconds, l.retryDelay)) {
        return `Retry delay must be ${l.retryDelay.min}–${l.retryDelay.max} seconds.`;
      }
      return '';
    },
    pacingSummary() {
      if (this.scheduleError) return this.scheduleError;
      if (this.pacingError) return this.pacingError;
      const f = this.form;
      const batch = `${f.batchSize} number${f.batchSize === 1 ? '' : 's'}`;
      let text = `Calls ${batch} every ${formatSeconds(f.intervalSeconds)}.`;
      if (f.maxAttempts > 1) {
        text += ` Unanswered numbers are retried after ${formatSeconds(f.retryDelaySeconds)}, up to ${f.maxAttempts} attempts.`;
      } else {
        text += ' Each number is tried once.';
      }
      return text;
    },
    hasNumbers() {
      return Boolean(this.form.csvFile) || parsePastedNumbers(this.form.pastedNumbers).length > 0;
    },
    canSubmit() {
      const f = this.form;
      return Boolean(f.name.trim() && f.agentId && f.sipTrunkId.trim())
        && !this.scheduleError
        && !this.pacingError
        && !(Array.isArray(this.csvError) ? this.csvError.length : this.csvError)
        && (!f.startNow || this.hasNumbers);
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.form = emptyForm();
        this.fileInputKey += 1;
        if (!this.agents.length) this.loadAgents();
      }
    },
  },
  methods: {
    async loadAgents() {
      this.loadingAgents = true;
      try {
        const response = await AgentService.getAllAgents();
        const list = response?.data || response || [];
        this.agents = Array.isArray(list) ? list : [];
      } catch (err) {
        notifyError(this.$store, err, 'Could not load agents.');
      } finally {
        this.loadingAgents = false;
      }
    },
    downloadTemplate() {
      downloadCsvTemplate();
    },
    onClose() {
      if (!this.saving) this.$emit('close');
    },
    buildBody() {
      const f = this.form;
      const body = {
        name: f.name.trim(),
        agent_id: f.agentId,
        sip_trunk_id: f.sipTrunkId.trim(),
        batch_size: f.batchSize,
        interval_seconds: f.intervalSeconds,
        max_attempts: f.maxAttempts,
        start: false,
      };
      if (f.maxAttempts > 1) body.retry_delay_seconds = f.retryDelaySeconds;
      const startAt = fromDateTimeLocal(f.startAt);
      const endAt = fromDateTimeLocal(f.endAt);
      if (startAt) body.start_at = startAt;
      if (endAt) body.end_at = endAt;
      const pasted = parsePastedNumbers(f.pastedNumbers);
      if (pasted.length) body.numbers = pasted;
      return body;
    },
    /**
     * Creates the campaign as a draft, uploads the CSV, then starts it when requested. Starting last
     * means the scheduler never sees a half-filled campaign.
     */
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      let created = null;
      const summary = { added: 0, duplicates: 0, rejected: [] };
      const addResult = (r) => {
        if (!r) return;
        summary.added += r.added || 0;
        summary.duplicates += r.duplicates || 0;
        summary.rejected.push(...(r.rejected || []));
      };

      try {
        created = await OutboundCampaignService.create(this.buildBody());
        addResult(created?.numbers);
      } catch (err) {
        notifyError(this.$store, err, 'Could not create the campaign.');
        this.saving = false;
        return;
      }

      const campaignId = created.campaign.id;
      try {
        if (this.form.csvFile) {
          addResult(await OutboundCampaignService.uploadNumbersCsv(campaignId, this.form.csvFile));
        }
        if (this.form.startNow) {
          await OutboundCampaignService.action(campaignId, 'start');
        }
        let message = `Campaign created with ${summary.added} number${summary.added === 1 ? '' : 's'}.`;
        if (summary.rejected.length) message += ` ${summary.rejected.length} rejected.`;
        if (summary.duplicates) message += ` ${summary.duplicates} duplicate skipped.`;
        notifySuccess(this.$store, message);
      } catch (err) {
        // The campaign exists; open it so the user can fix the numbers or start it from there.
        notifyError(this.$store, err, 'Campaign created, but adding numbers or starting it failed.');
      } finally {
        this.saving = false;
      }
      this.$emit('created', { campaignId, result: summary });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.campaign-drawer {
  .drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .drawer-header {
    border-bottom: 1px solid $light-border;

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
      gap: 24px;
    }

    .drawer-title {
      flex: 1;
      color: $light-foreground-1;
      font-weight: 700;
      font-size: $h5-size;
      line-height: 1.4;
      margin: 0;
    }

    .close-btn {
      background-color: $light-surface-2;
      width: 32px;
      height: 32px;
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 8px 24px 24px;
  }

  .form-section {
    padding: 16px 0;
    border-bottom: 1px solid $light-border;

    &.form-section--last {
      border-bottom: 0;
    }
  }

  .section-title-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  .section-title {
    font-size: $body-2-size;
    font-weight: 700;
    color: $light-foreground-1;
    margin: 0 0 12px;
  }

  .template-link {
    font-size: $body-3-size;
    color: $light-primary;
    text-decoration: none;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
  }

  .field-label {
    color: $light-foreground-1;
    font-weight: 600;
    font-size: $body-3-size;
  }

  .required {
    color: $light-primary;
  }

  .helper-text {
    font-size: $text-size-13;
    color: $light-foreground-3;
    margin: 0;
    line-height: 1.4;
  }

  .native-input {
    height: 40px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid $light-border;
    background: $light-surface-2;
    color: $light-foreground-1;
    font-family: inherit;
    font-size: $body-3-size;
    width: 100%;

    &:focus {
      outline: none;
      border-color: $light-primary;
    }
  }

  .pacing-summary {
    font-size: $body-3-size;
    color: $light-foreground-1;
    background: $light-surface-1;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 4px 0 0;
  }

  .drawer-footer {
    border-top: 1px solid $light-border;
    background-color: $light-surface-1;
    padding: 12px 24px;
    flex-shrink: 0;

    .button-group {
      display: flex;
      gap: 12px;

      .v-btn {
        flex: 1;
      }
    }
  }
}

::v-deep .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
